/** Phase floors for manuscript commenting progress (monotonic during a run). */
export const COMMENTING_PROGRESS = {
  started: 0.06,
  toolRunning: 0.12,
  toolComplete: 0.48,
  almostDone: 0.94,
  done: 1,
} as const;

const COMMENTS_APPLY_SPAN = COMMENTING_PROGRESS.almostDone - COMMENTING_PROGRESS.toolComplete;

export const COMMENTING_PROGRESS_TOOLS = [
  "list_custom_feedback_types",
  "mutate_manuscript_comments",
] as const;

export type CommentingProgressTool = (typeof COMMENTING_PROGRESS_TOOLS)[number];

export function isCommentingProgressTool(toolName: string): toolName is CommentingProgressTool {
  return (COMMENTING_PROGRESS_TOOLS as readonly string[]).includes(toolName);
}

export function mergeCommentingProgress(current: number, next: number): number {
  return Math.max(current, Math.min(1, next));
}

/** Heuristic target when total comment count is unknown upfront. */
export function estimateCommentingTargetCount(totalCreated: number, feedbackTypeCount: number): number {
  return Math.max(5, feedbackTypeCount * 3, totalCreated);
}

export function computeCommentingCommentsProgress(
  totalCreated: number,
  feedbackTypeCount: number,
): number {
  if (totalCreated <= 0) {
    return COMMENTING_PROGRESS.toolComplete;
  }
  const estimate = estimateCommentingTargetCount(totalCreated, feedbackTypeCount);
  const ratio = Math.min(1, totalCreated / estimate);
  return COMMENTING_PROGRESS.toolComplete + COMMENTS_APPLY_SPAN * ratio;
}

export function estimateCommentingToolDurationMs(toolName: string): number {
  if (toolName === "list_custom_feedback_types") return 8_000;
  if (toolName === "mutate_manuscript_comments") return 40_000;
  return 25_000;
}

export function computeCommentingToolRunningDisplayProgress(
  phaseProgress: number,
  toolStartedAt: number,
  toolName: string,
): number {
  const elapsed = Date.now() - toolStartedAt;
  const duration = estimateCommentingToolDurationMs(toolName);
  const ratio = Math.min(1, elapsed / duration);
  const creep = COMMENTING_PROGRESS.toolRunning
    + (COMMENTING_PROGRESS.toolComplete - COMMENTING_PROGRESS.toolRunning) * ratio;
  const capped = Math.min(COMMENTING_PROGRESS.toolComplete - 0.02, creep);
  return mergeCommentingProgress(phaseProgress, capped);
}
